<template>
  <TableV
    :actionBarButtons="actionBarButtons"
    :cols="cols"
    :disableTableSaveButton="disableTableSaveButton"
    :hasExportCSV="false"
    :lastRowData="lastRowData"
    :opts="opts"
    :rows="rows"
    @contextmenu="contextHandler"
    @save="doUpdate"
  >
    <div class="d-flex">
      <v-menu
        v-model="periodMenu"
        :close-on-click="false"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        offset-y
        right
        max-width="290px"
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            style="width: 370px"
            prefix="Período:"
            :value="period | toPeriod"
            dense
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            @click:prepend.stop="on.click"
            v-on="on"
            hide-details
            class="mb-n1 mt-0"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="period"
          range
          no-title
          @mouseenter:date="highlightWeek"
          @mouseleave:date="highlightWeekOver = []"
          :events="calendarEvents"
          @click:date="dateClick"
        ></v-date-picker>
      </v-menu>

      <v-autocomplete
        v-if="isAdmin"
        v-model="usuarioFiltroId"
        :items="opts.usuarios"
        class="mb-n1 mt-0 ml-3"
        style="width: 320px"
        item-value="id"
        item-text="nome"
        prefix="Usuário"
        prepend-icon="mdi-account"
        placeholder="Todos"
        dense
        clearable
        hide-details
        @change="debouncedLoad"
      ></v-autocomplete>
    </div>

    <FormModal
      :cols="cols"
      :errorMessage="createModalMessage"
      :opened.sync="openedCreateModal"
      :opts="optsModal"
      title="Alocar semana de trabalho"
      :value.sync="selectedItem"
      @save="createModalSave"
    />

    <GModal ref="confirmDeleteDay" title="Confirmar exclusão do dia">
      <p style="max-width: 500px">
        Deseja excluir o registro do dia <b>{{ selectedItem.dia }}</b> para o
        projeto <b>{{ selectedItem.projeto || "Título não encontrado" }}</b>
        <template v-if="selectedItem.usuarioNome">
          , usuário <b>{{ selectedItem.usuarioNome }}</b>
        </template>
        ?
      </p>
      <p>Essa ação não pode ser desfeita.</p>
      <template v-slot:buttons="{ close }">
        <v-spacer />
        <v-btn class="px-3" color="secondary" dark depressed @click="close(false)">Não</v-btn>
        <v-btn class="px-3" color="primary" dark depressed @click="close(true)">Sim</v-btn>
        <v-spacer />
      </template>
    </GModal>

    <GModal ref="confirmDeleteWeek" title="Confirmar exclusão">
      <p style="max-width: 500px">
        Deseja excluir todos os registros da semana para o projeto
        <b>{{ selectedItem.projeto || "Título não encontrado" }}</b>
        <template v-if="selectedItem.usuarioNome">
          , usuário <b>{{ selectedItem.usuarioNome }}</b>
        </template>
        ?
      </p>
      <p>Essa ação não pode ser desfeita.</p>
      <template v-slot:buttons="{ close }">
        <v-spacer />
        <v-btn class="px-3" color="secondary" dark depressed @click="close(false)">Não</v-btn>
        <v-btn class="px-3" color="primary" dark depressed @click="close(true)">Sim</v-btn>
        <v-spacer />
      </template>
    </GModal>
  </TableV>
</template>

<script>
import compareFloatNum from "@/helpers/compareFloatNum";
import ictTimesheetService from "@/api/ict/ictTimesheet.service";
import ictProjetosService from "@/api/ict/ictProjetos.service";
import ictUsuariosService from "@/api/ict/ictUsuarios.service";
import { UserTypeEnum } from "@/core/enums/user-types";
import { debounce } from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "IctAlocarHoras",
  components: {
    FormModal: () => import("@/components/form-modal.vue"),
    GModal: () => import("@/components/g-modal.vue"),
    TableV: () => import("@/components/table-v.vue"),
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    empresaId() {
      return this.clientId;
    },
    userId() {
      return this.user.id;
    },
    isAdmin() {
      return this.user.tipo_usuario !== UserTypeEnum.CLIENTE_CONSULTOR;
    },
    week() {
      return this.weekFromDate(this.period[0]);
    },
    actionBarButtons() {
      return [
        {
          text: "Adicionar",
          icon: "mdi-plus-box-outline",
          action: () => {
            if (this.projetosLoaded && this.projetosParaSemana.length === 0) {
              this.$notify({
                group: "geral",
                type: "warn",
                title: "Sem projetos disponíveis",
                text: "Nenhum projeto tem vigência ativa para a semana selecionada.",
              });
              return;
            }
            this.selectedItem = {};
            this.openedCreateModal = true;
            this.createModalMessage = null;
          },
        },
      ];
    },
    cols() {
      const days = this.week.map((day) => ({
        key: day,
        name: this.$options.filters.toWeekDay(day),
        type: this.$fieldTypes.TIME,
        editOnTable: true,
        editable: true,
        commentForField: `comment${day}`,
        align: 0,
        width: 120,
        colSize: 3,
        valueChanged: this.updateTotalCol,
      }));

      return [
        {
          key: "projetoId",
          name: "Projeto",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "projetos", key: "id", name: "nome_projeto" },
          hideInTable: true,
          rules: [{ rule: "required" }],
        },
        {
          key: "projeto",
          name: "Projeto",
          hideInform: true,
          type: this.$fieldTypes.TEXT,
        },
        {
          key: "usuarioId",
          name: "Usuário",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "usuarios", key: "id", name: "nome" },
          hideInTable: true,
          hideInform: !this.isAdmin,
        },
        {
          key: "usuarioNome",
          name: "Usuário",
          hideInform: true,
          hideInTable: !this.isAdmin,
          type: this.$fieldTypes.TEXT,
        },
        ...days,
        {
          key: "total",
          name: "Total",
          type: this.$fieldTypes.TIME,
          editable: false,
          hideInform: true,
        },
      ];
    },
    projetosParaSemana() {
      const [weekStart, weekEnd] = this.period;
      if (!weekStart || !weekEnd) return this.opts.projetos;
      return this.opts.projetos.filter((p) => {
        const inicio = p.vigencia_anobase_inicio || p.vigencia_total_inicio;
        const fim = p.vigencia_anobase_fim || p.vigencia_total_fim;
        if (!inicio || !fim) return true;
        return inicio <= weekEnd && fim >= weekStart;
      });
    },
    optsModal() {
      return { ...this.opts, projetos: this.projetosParaSemana };
    },
    lastRowData() {
      const totals = this.week.reduce(
        (prev, date) => ({ ...prev, [date]: 0 }),
        { usuarioNome: "Total", total: 0 }
      );
      return this.rows.reduce((acc, row) => {
        return [...this.week, "total"].reduce(
          (prev, day) => ({ ...prev, [day]: prev[day] + (row[day] ?? 0) }),
          acc
        );
      }, totals);
    },
  },
  created() {
    this.period = this.getWeekPeriod(moment());
    this.debouncedLoad = debounce(this.doLoad, 200);

    this.loadProjetos();

    if (this.isAdmin) {
      this.loadUsuarios();
    }

    this.debouncedLoad();
  },
  data() {
    return {
      createModalMessage: null,
      debouncedLoad: () => {},
      highlightWeekOver: [],
      openedCreateModal: false,
      period: [],
      periodMenu: false,
      projetosLoaded: false,
      rows: [],
      selectedItem: {},
      usuarioFiltroId: null,
      opts: {
        projetos: [],
        usuarios: [],
      },
    };
  },
  methods: {
    contextHandler(row, event, _expanded, col) {
      const options = [
        {
          name: "Excluir dia",
          limit: 1,
          show: moment(col.value, "YYYY-MM-DD").isValid(),
          cb: (row, col) => this.doDeleteDay(row, col),
        },
        {
          name: "Excluir semana",
          limit: 1,
          show: true,
          cb: (row) => this.doDeleteWeek(row),
        },
      ];
      this.showContext({
        left: event.clientX,
        top: event.clientY,
        val: row,
        col,
        menu: options.filter(({ show }) => show),
      });
    },
    async createModalSave(data, closeFn) {
      try {
        await this.doCreate(data);
        if (closeFn) closeFn();
      } catch (error) {
        const msg =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors)[0]?.[0]
            : error.response?.data?.message || "Falha ao salvar apontamento.";
        this.createModalMessage = msg;
      }
    },
    dateClick(day) {
      this.period = this.getWeekPeriod(moment(day, "YYYY-MM-DD"));
      this.periodMenu = this.period.length !== 2;
      if (!this.periodMenu) {
        this.debouncedLoad();
      }
    },
    disableTableSaveButton(row) {
      const datas = Object.values(row.originalData?.datas ?? {});
      const hasChanges = !this.week.some((day) => {
        const ts = datas.find(({ data }) => data === day);
        if (!ts) return row[day] > 0;
        const tempoPrev = ts.tempo || 0;
        const tempoPos = row[day] || 0;
        const ativPrev = ts.atividade ?? "";
        const ativPos = row[`comment${day}`] ?? "";
        return !compareFloatNum(tempoPos, tempoPrev) || ativPos !== ativPrev;
      });
      return hasChanges;
    },
    async doCreate(obj) {
      const body = this.formatTimeSheet(obj);
      if (!body.datas.length) {
        this.createModalMessage = "Insira pelo menos uma hora para criar o apontamento.";
        return;
      }
      await ictTimesheetService.saveSemanal(this.empresaId, body);
      await this.doLoad();
    },
    async doDeleteDay(row, col) {
      const datas = Object.values(row.originalData?.datas ?? {});
      const ts = datas.find(({ data }) => data === col.value);
      const id = ts?.id;

      if (!id) return;

      this.selectedItem = { dia: col.text, projeto: row.projeto, usuarioNome: row.usuarioNome };
      const confirm = await this.$refs.confirmDeleteDay.asyncOpen();
      this.selectedItem = {};

      if (!confirm) return;

      this.$root.$emit("addLoadingProcess", "ict-ts-delete-day");
      try {
        await ictTimesheetService.remove(this.empresaId, id);

        if (datas.length > 1) {
          row[col.value] = null;
          ts.tempo = null;
        } else {
          const index = this.rows.indexOf(row);
          if (index >= 0) this.rows.splice(index, 1);
        }
        this.updateTotalCol();
      } finally {
        this.$root.$emit("removeLoadingProcess", "ict-ts-delete-day");
      }
    },
    async doDeleteWeek(row) {
      this.selectedItem = { projeto: row.projeto, usuarioNome: row.usuarioNome };
      const confirm = await this.$refs.confirmDeleteWeek.asyncOpen();
      this.selectedItem = {};

      if (!confirm) return;

      this.$root.$emit("addLoadingProcess", "ict-ts-delete-week");
      try {
        const datas = Object.values(row.originalData?.datas ?? {});
        const ids = datas.map(({ id }) => id).filter(Boolean);
        await ictTimesheetService.deleteSemanal(this.empresaId, ids);

        const index = this.rows.indexOf(row);
        if (index >= 0) this.rows.splice(index, 1);
        this.updateTotalCol();
      } finally {
        this.$root.$emit("removeLoadingProcess", "ict-ts-delete-week");
      }
    },
    async doLoad() {
      this.$root.$emit("addLoadingProcess", "ict-ts-load");
      try {
        const [dataIni, dataFim] = this.period;
        let query = `dataIni=${dataIni}&dataFim=${dataFim}`;
        if (this.usuarioFiltroId) {
          query += `&usuarioId=${this.usuarioFiltroId}`;
        }
        const response = await ictTimesheetService.getSemanal(this.empresaId, { query });
        this.rows = this.parseTimeSheet(Array.isArray(response) ? response : []);
      } catch (_e) {
        this.rows = [];
      } finally {
        this.$root.$emit("removeLoadingProcess", "ict-ts-load");
      }
    },
    async doUpdate(row) {
      const body = this.formatTimeSheet(row);
      const response = await ictTimesheetService.saveSemanal(this.empresaId, body);
      const [nextRow] = this.parseTimeSheet([response]);
      const index = this.rows.indexOf(row);
      this.rows.splice(index, 1, nextRow);
      this.updateTotalCol();
      return response;
    },
    formatTimeSheet(row) {
      const { datas, ...props } = row;
      const nextDatas = this.week.reduce((acc, day) => {
        if (day in props && props[day] > 0) {
          const found = Object.values(datas ?? {}).find(({ data }) => data === day) ?? {};
          return [
            ...acc,
            {
              ...found,
              data: day,
              tempo: props[day],
              atividade: props[`comment${day}`] ?? "",
            },
          ];
        }
        return acc;
      }, []);

      this.week.forEach((day) => {
        delete props[day];
        delete props[`comment${day}`];
      });
      delete props.originalData;
      delete props.total;

      return {
        projeto_id: props.projetoId,
        usuario_id: props.usuarioId ?? this.userId,
        datas: nextDatas,
        data_ini: this.period[0],
        data_fim: this.period[1],
      };
    },
    getWeekPeriod(date) {
      const year = date.get("year");
      const month = date.get("month");
      let start = date.clone().day(0);
      let end = start.clone().day(6);

      if (start.get("year") !== year) start.set("year", year);
      if (end.get("year") !== year) end.set("year", year);
      if (start.get("month") !== month) start.set("month", month).startOf("month");
      if (end.get("month") !== month) end.set("month", month).endOf("month");

      return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
    },
    calendarEvents(date) {
      if (this.highlightWeekOver.includes(date)) return 'primary';
      const covered = this.opts.projetos.some((p) => {
        const inicio = p.vigencia_anobase_inicio || p.vigencia_total_inicio;
        const fim = p.vigencia_anobase_fim || p.vigencia_total_fim;
        if (!inicio || !fim) return false;
        return date >= inicio && date <= fim;
      });
      return covered ? 'success' : false;
    },
    highlightWeek(date) {
      this.highlightWeekOver = this.weekFromDate(date);
    },
    async loadProjetos() {
      try {
        const response = await ictProjetosService.selecao(this.empresaId);
        this.opts.projetos = Array.isArray(response) ? response : [];
      } catch (_e) {
        this.opts.projetos = [];
      } finally {
        this.projetosLoaded = true;
      }
    },
    async loadUsuarios() {
      try {
        const response = await ictUsuariosService.selecao(this.empresaId);
        this.opts.usuarios = Array.isArray(response) ? response : (response?.data || []);
      } catch (_e) {
        this.opts.usuarios = [];
      }
    },
    parseTimeSheet(timesheets) {
      if (!Array.isArray(timesheets)) return [];
      return timesheets.map((row) => {
        const datas = row.datas ? Object.values(row.datas) : [];
        return {
          ...row,
          originalData: row,
          total: datas.reduce((acc, { tempo }) => acc + (tempo || 0), 0),
          ...datas.reduce(
            (prev, { data, tempo, atividade }) => ({
              ...prev,
              [data]: tempo,
              [`comment${data}`]: atividade ?? "",
            }),
            {}
          ),
        };
      });
    },
    updateTotalCol() {
      if (!Array.isArray(this.rows)) return;
      this.rows = this.rows.map((row) => {
        const total = this.week.reduce((acc, date) => acc + (row[date] ?? 0), 0);
        return { ...row, total };
      });
    },
    weekFromDate(date) {
      const today = moment(date);
      const [start, end] = this.getWeekPeriod(today);
      const size = moment(end).diff(moment(start), "days");
      return new Array(size + 1)
        .fill(start)
        .map((_, index) => moment(start).add(index, "days").format("YYYY-MM-DD"));
    },
  },
  watch: {
    clientId() {
      this.projetosLoaded = false;
      this.loadProjetos();
      this.debouncedLoad();
      if (this.isAdmin) this.loadUsuarios();
    },
    period() {
      this.debouncedLoad();
    },
  },
};
</script>
